import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { ExtendedAttributeOption } from 'app/library/extended-attribute-options/models/extended-attribute-option';
import { ExtendedAttributeType } from 'app/library/extended-attributes/enums/extended-attribute-type.enum';
import { ExtendedAttribute } from 'app/library/extended-attributes/models/extended-attribute';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-attribute-with-options-filter',
    templateUrl: './attribute-with-options-filter.component.html',
    styleUrls: ['./attribute-with-options-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AttributeWithOptionsFilterComponent extends UnsubscribeOnDestroy implements OnInit, OnChanges {
    @Input()
    userAttribute: ExtendedAttribute;

    @ViewChild('optionsTableScrollHost', { read: ElementRef })
    private _optionsTableScrollHost?: ElementRef<HTMLElement>;

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[]>();

    dataSource: MatTableDataSource<ExtendedAttributeOption>;
    displayedColumns = ['name', 'buttons'];

    list: IChoosenFilterInterface[] = [];
    searchInput = new FormControl();
    extendedAttributeOptions: ExtendedAttributeOption[];
    ExtendedAttributeType = ExtendedAttributeType;

    constructor(private _allUsersService: AllUsersApiService, private _translateService: TranslateService) {
        super();
    }

    ngOnInit(): void {
        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
            this.updateDataSource();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['userAttribute'] && this.userAttribute?.options?.length) {
            this.updateDataSource();
        }
    }

    updateDataSource(): void {
        if (!this.userAttribute?.options?.length) {
            return;
        }

        this.dataSource = new MatTableDataSource(
            this.userAttribute.options.map((option) => {
                option = Object.assign(new ExtendedAttributeOption(), option, {
                    selected: this.list.some(
                        (item) => item.entity.id === option.id && item.entity.type === 'ExtendedAttributeOption'
                    ),
                });

                return option;
            })
        );

        this._scheduleScrollSelectedOptionIntoView();
    }

    private _scheduleScrollSelectedOptionIntoView(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const host = this._optionsTableScrollHost?.nativeElement;
                const selectedEl = host?.querySelector<HTMLElement>('[data-option-selected-row]');
                selectedEl?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            });
        });
    }

    addToList(extendedAttributeOption: ExtendedAttributeOption): void {
        this.addedToList.emit([
            ...this.list,
            {
                entity: { id: extendedAttributeOption.id, type: 'ExtendedAttributeOption' },
                valueToShow: `${this._translateService.instant(this.userAttribute.translationKey)}: ${
                    this.userAttribute.type === ExtendedAttributeType.Number
                        ? extendedAttributeOption.valueNumber
                        : this._translateService.instant(extendedAttributeOption.translationKey)
                }`,
                attributeName: this.userAttribute.name,
                attributeId: this.userAttribute.id,
                value: `${this.userAttribute.type === ExtendedAttributeType.Number ? extendedAttributeOption.valueNumber : extendedAttributeOption.valueText}`,
            },
        ]);
    }

    removeFromList(extendedAttributeOption: ExtendedAttributeOption): void {
        this.addedToList.emit(
            this.list.filter((item) => !(item.entity.id === extendedAttributeOption.id && item.entity.type === 'ExtendedAttributeOption'))
        );
    }
}
